import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import advancedFormat from 'dayjs/plugin/advancedFormat';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(advancedFormat);
dayjs.tz.setDefault('Asia/Kuala_Lumpur');

const DEFAULT_DATE_FORMAT = 'DD/MM/YYYY h:mm A';

export const DATA_TYPE = {
  ORDER: 'ORDER',
  PAYMENT_TRANSACTION: 'PAYMENT_TRANSACTION',
  REFUND_TRANSACTION: 'REFUND_TRANSACTION',
};

export const SEARCH_DB = {
  CASHIER: 'Cashier',
  GALAXY: 'Galaxy',
  SUPERNOVA: 'Supernova',
};

export const SEARCH_TYPES = {
  ORDER_ID: 'orderId',
  PAYMENT_REF_NUM: 'paymentReferenceNumber',
  AUTH_PSP_REF_ID: 'authPspReferenceId',
  PSP_REF_ID: 'pspReferenceId',
  REFUND_REFERENCE_NUM: 'refundReferenceNumber',
};

export const SEARCH_CONFIG = {
  [SEARCH_TYPES.ORDER_ID]: {
    label: 'Order ID',
    searchDBs: [SEARCH_DB.CASHIER],
  },
  [[SEARCH_TYPES.PAYMENT_REF_NUM]]: {
    label: 'Payment Reference Number',
    searchDBs: [SEARCH_DB.CASHIER, SEARCH_DB.GALAXY],
  },
  [[SEARCH_TYPES.AUTH_PSP_REF_ID]]: {
    label: 'Auth PSP Ref ID',
    searchDBs: [SEARCH_DB.CASHIER, SEARCH_DB.GALAXY],
  },
  [[SEARCH_TYPES.PSP_REF_ID]]: {
    label: 'PSP Ref ID',
    searchDBs: [SEARCH_DB.CASHIER, SEARCH_DB.GALAXY],
  },
  [[SEARCH_TYPES.REFUND_REFERENCE_NUM]]: {
    label: 'Refund Reference Number',
    searchDBs: [SEARCH_DB.CASHIER, SEARCH_DB.GALAXY],
  },
};

export const REACT_JSON_PROPS = {
  theme: 'rjv-default',
  indentWidth: 6,
  collapsed: 2,
  editable: false,
  sortKeys: true,
  quotesOnKeys: false,
  displayDataTypes: false,
};

export const ORDER_DETAILS_COLUMNS = [
  {
    field: 'orderId',
    headerName: 'Order Id',
    style: { paddingLeft: 0 },
  },
  {
    field: 'orderType',
    headerName: 'Order Type',
  },
  {
    field: 'emailId',
    headerName: 'Email',
  },
  {
    field: 'orderSummary.fare.baseCurrency',
    headerName: 'Base Currency Amount',
    valueGetter: ({ row }) =>
      `${row.orderSummary.fare.baseCurrency.currency} ${row.orderSummary.fare.baseCurrency.totalAmount}`,
    hideIf: ({ row }) =>
      !(
        row?.orderSummary?.fare?.baseCurrency.currency ||
        row?.orderSummary?.fare?.baseCurrency.totalAmount
      ),
    align: 'right',
  },
  {
    field: 'orderSummary.fare.invoiceCurrency',
    headerName: 'Invoice Currency Amount',
    valueGetter: ({ row }) =>
      `${row.orderSummary.fare.invoiceCurrency.currency} ${row.orderSummary.fare.invoiceCurrency.totalAmount}`,
    hideIf: ({ row }) =>
      !(
        row?.orderSummary?.fare?.invoiceCurrency.currency ||
        row?.orderSummary?.fare?.invoiceCurrency.totalAmount
      ),
    align: 'right',
  },
  {
    field: 'createdAt',
    headerName: 'Created At (in MYT)',
    valueGetter: ({ row }) => dayjs(row.createdAt).format(DEFAULT_DATE_FORMAT),
  },
  {
    field: 'action',
    headerName: 'Action',
    valueGetter: ({ row }) => row.orderSummary,
    showJSON: true,
    align: 'center',
  },
];

export const CASHIER_TRANSACTION_COLUMNS = [
  {
    field: 'paymentReferenceNumber',
    headerName: 'Payment Reference Number',
  },
  {
    field: 'transactionId',
    headerName: 'Transaction Id',
  },
  {
    field: 'resultCode',
    headerName: 'Result Code',
  },
  {
    field: 'paymentMethodType',
    headerName: 'Method Type',
  },
  {
    field: 'payoptMethodCode',
    headerName: 'Method',
    valueGetter: ({ row }) => `${row.methodName} (${row.payoptMethodCode})`,
    hideIf: ({ row }) => !(row?.methodName || row?.payoptMethodCode),
  },
  {
    field: 'error',
    headerName: 'Error',
    valueGetter: ({ row }) => `${row.errorCode} ${row.errorReason}`,
    hideIf: ({ row }) => !row?.errorCode && !row?.errorReason,
  },
  {
    field: 'amount',
    headerName: 'Transaction Amount',
    valueGetter: ({ row }) => `${row.currency} ${row.totalOrderAmount}`,
    align: 'right',
  },
  {
    field: 'paymentGatewayId',
    headerName: 'Gateway Id',
  },
  {
    field: 'createdAt',
    headerName: 'Created At (in MYT)',
    valueGetter: ({ row }) => dayjs(row.createdAt).format(DEFAULT_DATE_FORMAT),
  },
  {
    field: 'action',
    headerName: 'Action',
    valueGetter: ({ row }) => row,
    showJSON: true,
    align: 'center',
  },
];

export const GALAXY_TRANSACTION_COLUMNS = [
  {
    field: 'orderType',
    headerName: 'Order Type',
  },
  {
    field: 'paymentReferenceNumber',
    headerName: 'Payment Reference Number',
  },
  {
    field: 'transactionId',
    headerName: 'Transaction Id',
  },
  {
    field: 'status',
    headerName: 'Status',
  },
  {
    field: 'paymentMethodType',
    headerName: 'Method Type',
  },
  {
    field: 'methodName',
    headerName: 'Method Name',
  },
  {
    field: 'payoptMethodCode',
    headerName: 'Method Code',
    hideIf: ({ row }) => !row?.payoptMethodCode,
  },
  {
    field: 'amount',
    headerName: 'Transaction Amount',
    valueGetter: ({ row }) => `${row.currency} ${row.amount}`,
    align: 'right',
  },
  {
    field: 'error',
    headerName: 'Error',
    valueGetter: ({ row }) => `${row.errorCode} ${row.errorReason}`,
    hideIf: ({ row }) => !row?.errorCode && !row?.errorReason,
  },
  {
    field: 'gatewayCode',
    headerName: 'Gateway Code',
    hideIf: ({ row }) => !row?.gatewayCode,
  },
  {
    field: 'paymentGatewayId',
    headerName: 'Payment Gateway Id',
    hideIf: ({ row }) => !row?.paymentGatewayId,
  },
  {
    field: 'createdAt',
    headerName: 'Created At (in MYT)',
    valueGetter: ({ row }) => dayjs(row.createdAt).format(DEFAULT_DATE_FORMAT),
  },
  {
    field: 'action',
    headerName: 'Action',
    valueGetter: ({ row }) => row,
    showJSON: true,
    align: 'center',
  },
];

export const CASHIER_REFUND_COLUMNS = [
  {
    field: 'refundId',
    headerName: 'Refund Id',
  },
  {
    field: 'paymentReferenceNumber',
    headerName: 'Payment Reference Number',
  },
  {
    field: 'transactionId',
    headerName: 'Transaction Id',
  },
  {
    field: 'resultCode',
    headerName: 'Result Code',
  },
  {
    field: 'errorReason',
    headerName: 'Error Reason',
    valueGetter: ({ row }) => `${row.errorCode} ${row.errorReason}`,
    hideIf: ({ row }) => !row?.errorCode && !row?.errorReason,
  },
  {
    field: 'amount',
    headerName: 'Transaction Amount',
    valueGetter: ({ row }) => `${row.currency} ${row.amount}`,
    align: 'right',
  },
  {
    field: 'gatewayCode',
    headerName: 'Gateway Code',
    hideIf: ({ row }) => !row?.gatewayCode,
  },
  {
    field: 'paymentGatewayId',
    headerName: 'Payment Gateway Id',
    hideIf: ({ row }) => !row?.paymentGatewayId,
  },
  {
    field: 'createdAt',
    headerName: 'Created At (in MYT)',
    valueGetter: ({ row }) => dayjs(row.createdAt).format(DEFAULT_DATE_FORMAT),
  },
  {
    field: 'action',
    headerName: 'Action',
    valueGetter: ({ row }) => row,
    showJSON: true,
    align: 'center',
  },
];

export const GALAXY_REFUND_COLUMNS = [
  {
    field: 'paymentReferenceNumber',
    headerName: 'Payment Reference Number',
  },
  {
    field: 'transactionId',
    headerName: 'Transaction Id',
  },
  {
    field: 'errorReason',
    headerName: 'Error Reason',
    valueGetter: ({ row }) => `${row.errorCode} ${row.errorReason}`,
    hideIf: ({ row }) => !row?.errorCode && !row?.errorReason,
  },
  {
    field: 'refundMethodType',
    headerName: 'Refund Method Type',
  },
  {
    field: 'status',
    headerName: 'Status',
  },
  {
    field: 'amount',
    headerName: 'Transaction Amount',
    valueGetter: ({ row }) => `${row.currency} ${row.amount}`,
    align: 'right',
  },
  {
    field: 'gatewayCode',
    headerName: 'Gateway Code',
    hideIf: ({ row }) => !row?.gatewayCode,
  },
  {
    field: 'paymentGatewayId',
    headerName: 'Payment Gateway Id',
    hideIf: ({ row }) => !row?.paymentGatewayId,
  },
  {
    field: 'createdAt',
    headerName: 'Created At (in MYT)',
    valueGetter: ({ row }) => dayjs(row.createdAt).format(DEFAULT_DATE_FORMAT),
  },
  {
    field: 'action',
    headerName: 'Action',
    valueGetter: ({ row }) => row,
    showJSON: true,
    align: 'center',
  },
];
